import React, { useState } from "react";
import AttachmentPreview from './components/AttachmentPreview';
import { colors } from "../../../config/colors";
import { getStatusColor } from './utils/CommonFunctions';
import JobService from "../../../../services/JobService";
import { toast } from "react-toastify";
import { getJobTitleLabel } from "../../../utils/jobTitleLabel";
// import JobTimeline from'./components/JobTimeline';

const TASK_STATUS_OPTIONS = [
    { label: "Pending", value: "PENDING" },
    { label: "Done", value: "DONE" },
    { label: "Skip", value: "SKIP" },
    { label: "Cancelled", value: "CANCELLED" },
];

const JOB_STATUS_OPTIONS = [
    { label: "Pending", value: "PENDING" },
    { label: "Driving", value: "DRIVING" },
    { label: "Arrived", value: "ARRIVED" },
    { label: "Started", value: "STARTED" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Skipped", value: "SKIPPED" },
    { label: "Cancelled", value: "CANCELLED" },
    { label: "Failed", value: "FAILED" },
];

const JobUpdates = ({ job, onJobUpdated }) => {
    const jobLabel = getJobTitleLabel();
    const [updatingTaskId, setUpdatingTaskId] = useState(null);
    const [taskStatusDrafts, setTaskStatusDrafts] = useState({});
    const [jobStatusDraft, setJobStatusDraft] = useState(job.status);
    const [updatingJobStatus, setUpdatingJobStatus] = useState(false);

    // Extract driving and arrived times from job.timeline
    const drivingEvent = job.timeline?.find(
        (item) => item.type === 'JOB_STATUS' && item.status === 'DRIVING'
    );
    const arrivedEvent = job.timeline?.find(
        (item) => item.type === 'JOB_STATUS' && item.status === 'ARRIVED'
    );
    // Find job started timestamp
    const jobStartedEvent = job.timeline?.find(
        (item) => item.type === 'JOB_STATUS' && item.status === 'STARTED'
    );

    const handleUpdateTaskStatus = async (task, newStatus) => {
        if (!newStatus) return;
        try {
            setUpdatingTaskId(task._id);
            const prevStatus = task.status;

            // Build the standardized status change note
            const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
            const updatedByName = userDetails?.user?.name || "Admin";
            const updatedByRole = userDetails?.user?.role
                ? userDetails.user.role.charAt(0) + userDetails.user.role.slice(1).toLowerCase()
                : "Admin";
            const timestamp = new Date().toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            }).replace(",", "");
            const noteContent = `Status changed: ${prevStatus} → ${newStatus} • By ${updatedByName} (${updatedByRole}) • ${timestamp}`;

            const payloadTask = {
                _id: task._id,
                taskDetails: task.taskDetails,
                status: newStatus,
                timeSpent: task.timeSpent != null ? String(task.timeSpent) : "0",
                notes: [
                    ...(task.notes || []),
                    { content: noteContent }
                ],
                attachments: task.attachments || [],
                instanceIndex: task.instanceIndex,
                quantity: task.quantity,
                reason: task.reason,
            };
            const payload = {
                employeeTasks: [payloadTask],
            };
            const response = await JobService.workJob(job._id, payload);
            if (response.success) {
                toast.success("Task status updated");
                if (onJobUpdated) {
                    onJobUpdated();
                }
            } else {
                toast.error(response.message || "Failed to update task");
            }
        } catch (error) {
            console.error("Error updating task via work endpoint:", error);
            toast.error(error?.response?.data?.message || "Failed to update task");
        } finally {
            setUpdatingTaskId(null);
        }
    };

    const handleUpdateJobStatus = async () => {
        if (!jobStatusDraft || jobStatusDraft === job.status) return;
        try {
            setUpdatingJobStatus(true);
            const response = await JobService.updateJob(job._id, { status: jobStatusDraft });
            if (response.success || response.data?.success) {
                toast.success(`${jobLabel} status updated to ${jobStatusDraft}`);
                if (onJobUpdated) onJobUpdated();
            } else {
                toast.error(response.message || `Failed to update ${jobLabel.toLowerCase()} status`);
            }
        } catch (error) {
            console.error("Error updating job status:", error);
            toast.error(error?.response?.data?.message || `Failed to update ${jobLabel.toLowerCase()} status`);
        } finally {
            setUpdatingJobStatus(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <h3
                        className="text-xl font-semibold"
                        style={{ color: colors.primary.headingsBody }}
                    >
                        {`${jobLabel} Progress Overview`}
                    </h3>
                    <div className="flex items-center gap-3">
                        <div
                            className={`px-4 py-2 rounded-full ${getStatusColor(job.status)} border text-sm font-medium`}
                        >
                            {job.status}
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                                value={jobStatusDraft}
                                onChange={(e) => setJobStatusDraft(e.target.value)}
                                disabled={updatingJobStatus}
                            >
                                {JOB_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={handleUpdateJobStatus}
                                disabled={updatingJobStatus || jobStatusDraft === job.status}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-[#166473] hover:bg-[#1a7a8c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {updatingJobStatus ? "Updating..." : "Update Status"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                        <div className="text-sm text-blue-700 mb-1">
                            Actual Start Time
                        </div>
                        <div className="font-medium">
                            {job.actualStartTime
                                ? new Date(job.actualStartTime).toLocaleString(
                                    "en-US",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                    }
                                )
                                : "Not recorded"}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                        <div className="text-sm text-green-700 mb-1">
                            Actual End Time
                        </div>
                        <div className="font-medium">
                            {job.actualEndTime
                                ? new Date(job.actualEndTime).toLocaleString(
                                    "en-US",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                    }
                                )
                                : "Not recorded"}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                        <div className="text-sm text-yellow-700 mb-1">
                            Started Driving At
                        </div>
                        <div className="font-medium">
                            {drivingEvent?.timestamp
                                ? new Date(drivingEvent.timestamp).toLocaleString(
                                    "en-US",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                    }
                                )
                                : "Not recorded"}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200">
                        <div className="text-sm text-pink-700 mb-1">
                            Arrived At
                        </div>
                        <div className="font-medium">
                            {arrivedEvent?.timestamp
                                ? new Date(arrivedEvent.timestamp).toLocaleString(
                                    "en-US",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                        year: "numeric",
                                        month: "2-digit",
                                        day: "2-digit",
                                    }
                                )
                                : "Not recorded"}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                        <div className="text-sm text-purple-700 mb-1">
                            Duration
                        </div>
                        <div className="font-medium">
                            {job.actualStartTime && job.actualEndTime
                                ? `${Math.round((new Date(job.actualEndTime) - new Date(job.actualStartTime)) / 1000 / 60)} minutes`
                                : "Not available"}
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="mb-8">
                <h4 className="text-lg font-semibold mb-6" style={{ color: colors.primary.headingsBody }}>{jobLabel} Timeline</h4>
                <JobTimeline timeline={job.timeline} tasks={job.tasks} />
              </div> */}

            <div className="mb-8">
                <h4
                    className="text-lg font-semibold mb-6"
                    style={{ color: colors.primary.headingsBody }}
                >
                    Task Progress
                </h4>
                <div className="grid grid-cols-1 gap-6">
                    {job.employeeTasks.map((task, index) => {
                        const taskDetails = job.tasks.find(
                            (t) => t.taskId._id === task.taskId
                        )?.taskId;
                        // Find DONE event for this employee task
                        const taskDoneEvent = job.timeline?.find(
                            (item) => item.type === 'TASK_STATUS' && item.status === 'DONE' && item.taskId === task._id
                        );
                        // Use the timeSpent from the timeline event (already in minutes)
                        let timeSpentDisplay = "Not available";
                        if (taskDoneEvent?.timeSpent != null) {
                            const totalMinutes = Number(taskDoneEvent.timeSpent);
                            const minutes = Math.floor(totalMinutes);
                            const seconds = Math.round((totalMinutes - minutes) * 60);
                            if (minutes > 0 && seconds > 0) {
                                timeSpentDisplay = `${minutes} minute${minutes > 1 ? 's' : ''} and ${seconds} second${seconds > 1 ? 's' : ''}`;
                            } else if (minutes > 0) {
                                timeSpentDisplay = `${minutes} minute${minutes > 1 ? 's' : ''}`;
                            } else if (seconds > 0) {
                                timeSpentDisplay = `${seconds} second${seconds > 1 ? 's' : ''}`;
                            } else {
                                timeSpentDisplay = '0 seconds';
                            }
                        }
                        return (
                            <div
                                key={task._id}
                                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                            >
                                <div className="p-4 bg-gray-50 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h5 className="font-medium text-gray-900">
                                                {task.taskDetails?.title}
                                            </h5>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Instance{" "}
                                                {task.instanceIndex + 1}
                                            </p>
                                        </div>
                                        <div
                                            className={`px-3 py-1 rounded-full text-sm ${getStatusColor(task.status)}`}
                                        >
                                            {task.status}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <div className="text-sm text-gray-500">
                                                Time Spent
                                            </div>
                                            <div className="font-medium">
                                                {timeSpentDisplay}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500">
                                                Cost
                                            </div>
                                            <div className="font-medium">
                                                ${task.taskDetails?.cost || 0}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-start justify-end gap-2">
                                            <div className="text-sm text-gray-500">
                                                Status
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <select
                                                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                                                    value={taskStatusDrafts[task._id] || task.status}
                                                    onChange={(e) =>
                                                        setTaskStatusDrafts((prev) => ({
                                                            ...prev,
                                                            [task._id]: e.target.value,
                                                        }))
                                                    }
                                                    disabled={updatingTaskId === task._id}
                                                >
                                                    {TASK_STATUS_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleUpdateTaskStatus(
                                                            task,
                                                            taskStatusDrafts[task._id] || task.status
                                                        )
                                                    }
                                                    disabled={updatingTaskId === task._id}
                                                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-60"
                                                >
                                                    {updatingTaskId === task._id ? "Updating..." : "Update"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {task.notes.length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="text-sm font-medium text-gray-700 mb-2">
                                                Notes
                                            </h6>
                                            <div className="space-y-2">
                                                {task.notes.map((note) => (
                                                    <div
                                                        key={note._id}
                                                        className="bg-blue-50 p-3 rounded-md border border-blue-100"
                                                    >
                                                        <p className="text-sm text-gray-900">
                                                            {note.content}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {new Date(
                                                                note.createdAt
                                                            ).toLocaleString()}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {task.attachments.length > 0 && (
                                        <div>
                                            <h6 className="text-sm font-medium text-gray-700 mb-2">
                                                Attachments
                                            </h6>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                                {task.attachments.map(
                                                    (attachment) => (
                                                        <AttachmentPreview
                                                            key={attachment._id}
                                                            attachment={
                                                                attachment
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {job.notes.length > 0 && (
                <div className="mb-8">
                    <h4
                        className="text-lg font-semibold mb-6"
                        style={{ color: colors.primary.headingsBody }}
                    >
                        General Notes
                    </h4>
                    <div className="space-y-4">
                        {job.notes.map((note) => (
                            <div
                                key={note._id}
                                className="bg-white p-4 rounded-lg border border-gray-200"
                            >
                                <p className="text-gray-900">{note.content}</p>
                                <p className="text-sm text-gray-500 mt-2">
                                    {new Date(note.createdAt).toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {job.workAttachments.length > 0 && (
                <div>
                    <h4
                        className="text-lg font-semibold mb-6"
                        style={{ color: colors.primary.headingsBody }}
                    >
                        Work Attachments
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {job.workAttachments.map((attachment) => (
                            <AttachmentPreview
                                key={attachment._id}
                                attachment={attachment}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobUpdates;
